import React from "react";
import {
  getAccountDisplayName,
  getAccountReferenceId,
  isAdsAccount,
} from "../shared/utils/account-display";
import { cn } from "./ui/cn";

/**
 * Renders account display name with optional ad account ID for disambiguation.
 * @param {'auto'|'always'|'never'} showId - auto shows ID for ads accounts only
 */
export default function AccountLabel({
  account,
  className,
  nameClassName,
  idClassName,
  showId = "auto",
  layout = "stack",
  title,
}) {
  const name = getAccountDisplayName(account);
  const refId = getAccountReferenceId(account);
  const shouldShowId =
    showId === "always" ? Boolean(refId || account?.providerAccountId) :
    showId === "never" ? false :
    Boolean(refId);

  const displayId =
    refId ||
    (showId === "always" && isAdsAccount(account) ? account?.providerAccountId : null);

  const tooltip = title ?? (displayId ? `${name} · ${displayId}` : name);

  if (!shouldShowId || !displayId) {
    return (
      <span className={cn("truncate", className, nameClassName)} title={tooltip}>
        {name}
      </span>
    );
  }

  if (layout === "inline") {
    return (
      <span className={cn("min-w-0 truncate", className)} title={tooltip}>
        <span className={nameClassName}>{name}</span>
        <span className={cn("font-mono text-slate-400", idClassName)}> · {displayId}</span>
      </span>
    );
  }

  return (
    <div className={cn("min-w-0", className)} title={tooltip}>
      <p className={cn("truncate text-sm font-medium text-slate-800", nameClassName)}>
        {name}
      </p>
      <p className={cn("truncate font-mono text-[11px] text-slate-400", idClassName)}>
        {displayId}
      </p>
    </div>
  );
}

/** Compact pill for ad account ID in lists and dropdowns. */
export function AccountIdBadge({ account, className }) {
  const refId = getAccountReferenceId(account);
  if (!refId) return null;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200/80",
        className
      )}
      title={`Account ID: ${refId}`}
    >
      {refId}
    </span>
  );
}
